// Die Slugs kommen unübersetzt vom Backend (GET /api/jobs/options bzw.
// backend/config/constants.js). Die deutschen Labels stehen nur hier.
// Die Keys sind gleichzeitig der Fallback, falls /jobs/options nicht lädt.

export const JOB_TYPE_LABELS = {
  schulpraktikum: 'Schulpraktikum',
  praktikum: 'Praktikum',
  ausbildung: 'Ausbildung',
  duales_studium: 'Duales Studium',
  werkstudent: 'Werkstudent',
  aushilfe: 'Aushilfe / Minijob',
  vollzeit: 'Vollzeit',
};

export const BRANCHE_LABELS = {
  gastronomie: 'Gastronomie',
  einzelhandel: 'Einzelhandel',
  handwerk: 'Handwerk',
  pflege_gesundheit: 'Pflege & Gesundheit',
  buero_verwaltung: 'Büro & Verwaltung',
  logistik_lager: 'Logistik & Lager',
  produktion: 'Produktion',
  kinderbetreuung_soziales: 'Kinderbetreuung & Soziales',
  landwirtschaft_weinbau: 'Landwirtschaft & Weinbau',
  hotel_tourismus: 'Hotel & Tourismus',
  reinigung: 'Reinigung',
  friseur_kosmetik: 'Friseur & Kosmetik',
  kfz: 'Kfz',
  garten_landschaftsbau: 'Garten- & Landschaftsbau',
  bildung_nachhilfe: 'Bildung & Nachhilfe',
  sport_freizeit: 'Sport & Freizeit',
  sicherheit: 'Sicherheit',
  sonstiges: 'Sonstiges',
};

export const WEEKDAY_LABELS = {
  mo: 'Mo', di: 'Di', mi: 'Mi', do: 'Do', fr: 'Fr', sa: 'Sa', so: 'So',
};

// Wochenende ist ein Arbeitstag (sa, so), keine Tageszeit
export const DAYTIME_LABELS = {
  vormittags: 'Vormittags',
  nachmittags: 'Nachmittags',
  abends: 'Abends',
};

// Unbekannte Slugs unübersetzt durchreichen statt leer anzuzeigen
export const labelOf = (map, slug) => map[slug] || slug || '';

// Arbeitstage: Schnellauswahl über den einzelnen Tagen. Gespeichert wird nur
// die Tagesliste. Flexibel = keine festen Tage = leere Liste, die beim Filtern
// nie ausgeschlossen wird.
const WEEKDAYS = Object.keys(WEEKDAY_LABELS);
export const WORKDAY_PRESETS = [
  { key: 'mofr', label: 'Mo–Fr', days: WEEKDAYS.slice(0, 5) },
  { key: 'wochenende', label: 'Wochenende', days: ['sa', 'so'] },
  { key: 'woche', label: 'Ganze Woche', days: WEEKDAYS },
  { key: 'flexibel', label: 'Flexibel', days: [] },
];

// Aktiver Chip oben: der kleinste Bereich, der alle gewählten Tage enthält
export const workdayPreset = (days = []) =>
  days.length
    ? WORKDAY_PRESETS.slice(0, 3).find(p => days.every(d => p.days.includes(d))) || WORKDAY_PRESETS[2]
    : WORKDAY_PRESETS[3];

// "Mo–Fr", "Wochenende", … oder die einzelnen Tage ("Mo, Mi, Fr")
export const weekdaysLabel = (days = []) => {
  const preset = workdayPreset(days);
  return days.length === preset.days.length
    ? preset.label
    : WEEKDAYS.filter(d => days.includes(d)).map(d => WEEKDAY_LABELS[d]).join(', ');
};

// Skills am Inserat: reine Beschreibung, kein Filter. Gespeichert werden die
// Labels selbst (jobs.skills ist TEXT[]), die Gruppe ergibt sich hieraus.
export const JOB_SKILL_GROUPS = [
  { title: 'PC & Office', skills: ['Word', 'Excel', 'PowerPoint', 'E-Mail & Outlook', '10-Finger-Schreiben', 'Internetrecherche', 'Kassensystem'] },
  { title: 'Sprachen', skills: ['Deutsch', 'Englisch', 'Französisch', 'Türkisch', 'Arabisch', 'Russisch', 'Polnisch', 'Italienisch', 'Ukrainisch'] },
  { title: 'Handwerk & Technik', skills: ['Handwerkliches Geschick', 'Umgang mit Werkzeug', 'Technisches Verständnis', 'Maschinen bedienen', 'Montage & Aufbau', 'Streichen & Lackieren', 'Gartenarbeit'] },
  { title: 'Persönlich', skills: ['Zuverlässig', 'Pünktlich', 'Teamfähig', 'Freundlich', 'Belastbar', 'Selbstständig', 'Sorgfältig', 'Lernbereit', 'Kundenorientiert'] },
  { title: 'Führerschein', skills: ['Mofa-Prüfbescheinigung', 'Klasse AM (Roller)', 'Klasse A1 (Leichtkraftrad)', 'Klasse B (Auto)', 'B17 (begleitetes Fahren)', 'Klasse L (Traktor)', 'Staplerschein'] },
];

// Für die Anzeige: nur Gruppen mit gewählten Skills, Unbekanntes unter "Weitere"
export const groupSkills = (skills = []) => {
  const groups = JOB_SKILL_GROUPS
    .map(g => ({ title: g.title, skills: g.skills.filter(s => skills.includes(s)) }))
    .filter(g => g.skills.length);
  const known = JOB_SKILL_GROUPS.flatMap(g => g.skills);
  const rest = skills.filter(s => !known.includes(s));
  return rest.length ? [...groups, { title: 'Weitere', skills: rest }] : groups;
};

// Jede Stellenbezeichnung trägt "(m/w/d)". Steht schon eine Variante im Titel
// (m/w/d, (w/m/d), m/f/d, m/w/x, m/w/divers, gn, all genders), kommt nichts dazu.
const GENDER_TAG = /(^|[^a-zäöüß])[mwfdxi]\s*[/|]\s*[mwfdxi]\s*[/|]\s*(?:div(?:ers)?|[mwfdxi])(?![a-zäöüß])|\(\s*gn\s*\)|all\s*genders/i;
export const hasGenderTag = (title = '') => GENDER_TAG.test(title);
export const jobTitle = (title) => {
  const t = (title || '').trim();
  return !t || hasGenderTag(t) ? t : `${t} (m/w/d)`;
};

export const VOLLZEIT_HINT =
  'Vollzeitstellen sind hier nur für Einstiegspositionen ohne geforderte Ausbildung gedacht.';
